/** @format */

import {ExclamationIcon, InformationCircleIcon} from '@heroicons/react/outline'
import React, {useContext, useState} from 'react'

import ajax from '../../ajax'
import Button from '../../components/tailwind/Button'
import Input from '../../components/tailwind/Input'
import LoaderButton from '../../components/tailwind/LoaderButton'
import {ModalContext} from '../../components/tailwind/ModalContext'
import {AccountContext} from '../../contexts/AccountContext'
import {LoadingContext} from '../../contexts/LoadingContext'
import * as toast from '../../toast'
import {isValidEmail} from '../../utils'

export default function TransferAdminModal({onTransferSent}) {
  const {closeCurrentModal} = useContext(ModalContext)
  const {ready} = useContext(LoadingContext)
  const {account} = useContext(AccountContext)
  const atCapacity =
    account.member_count >= account.limits.team_members.limit
  const [email, setEmail] = useState('')
  const [transfer, setTransfer] = useState(null)
  const [confirmReplace, setConfirmReplace] = useState(false)

  async function submitTransfer({force = false} = {}) {
    await ajax({
      method: 'POST',
      endpoint: '/api-int/team/transfer-admin',
      payload: {email, force},
      onSuccess: response => {
        setConfirmReplace(false)
        setTransfer(response)
      },
      onError: (_body, resp) => {
        if (resp && resp.status === 409) {
          setConfirmReplace(true)
        } else {
          toast.error('Failed to transfer primary admin')
        }
        ready()
      },
    })
    ready()
  }

  if (transfer) {
    return (
      <>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
            <InformationCircleIcon
              className="h-6 w-6 text-blue-600"
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <p className="text-sm font-medium text-gray-900">
              Ownership transfer invitation sent
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {
                "We've emailed the new owner with a link to accept the transfer."
              }
            </p>
            {transfer.will_remove_initiator ? (
              <p className="mt-2 text-sm text-gray-500">
                You will be removed from the team when this transfer is
                accepted.
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <Button
            primary
            className="w-full px-4 py-2 text-base sm:ml-3 sm:w-auto"
            onClick={() => {
              onTransferSent?.()
              closeCurrentModal()
            }}
          >
            Done
          </Button>
        </div>
      </>
    )
  }

  if (confirmReplace) {
    return (
      <>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationIcon
              className="h-6 w-6 text-yellow-600"
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <p className="text-sm font-medium text-gray-900">
              Transfer already in progress
            </p>
            <p className="mt-2 text-sm text-gray-500">
              A transfer invitation is already pending for this team. Sending a
              new invitation will cancel the existing one.
            </p>
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <LoaderButton
            className="inline-flex w-full px-4 py-2 text-base sm:ml-3 sm:w-auto"
            onClick={() => submitTransfer({force: true})}
          >
            Send new invitation
          </LoaderButton>
          <Button
            className="mt-3 inline-flex w-full px-4 py-2 text-base sm:mt-0 sm:w-auto"
            onClick={() => closeCurrentModal()}
          >
            Cancel
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
          <InformationCircleIcon
            className="h-6 w-6 text-blue-600"
            aria-hidden="true"
          />
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <p className="text-sm text-gray-500">
            {
              "Transfer primary admin access to another user by email. They'll receive a link to accept ownership of this team."
            }
          </p>
        </div>
      </div>
      {atCapacity ? (
        <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3">
          <div className="flex">
            <ExclamationIcon
              className="h-5 w-5 shrink-0 text-yellow-600"
              aria-hidden="true"
            />
            <p className="ml-2 text-sm text-yellow-700">
              Your team has no open member slots. If the new admin is not
              already a team member, you will be removed from the team.
            </p>
          </div>
        </div>
      ) : null}
      <p className="mt-4 text-sm text-gray-500">
        {
          "Enter the email address of the user you'd like to make the new primary admin."
        }
      </p>
      <div>
        <Input
          autoFocus
          placeholder="you@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <LoaderButton
          className="inline-flex w-full px-4 py-2 text-base sm:ml-3 sm:w-auto"
          disabled={!isValidEmail(email)}
          onClick={() => submitTransfer()}
        >
          Transfer
        </LoaderButton>
      </div>
    </>
  )
}
